import { useMemo, useState } from 'react';
import { Clock } from 'lucide-react';
import { usePersonalHours } from '@/hooks/useEntries';
import { useSession } from '@/hooks/useSession';
import { formatDay, hoursShort } from '@/lib/format';
import type { PersonalHoursPeriod } from '@/types';
import { Screen } from '@m/ui/Screen';
import { Group, Row } from '@m/ui/List';
import { ChipRow, Empty, Segmented, SkeletonList, Stat } from '@m/ui/Controls';
import { wholeMoney } from '../clients/useUnbilled';

const PERIODS: { value: PersonalHoursPeriod; label: string }[] = [
  { value: 'current-pay', label: 'This pay period' },
  { value: 'last-pay', label: 'Last pay period' },
  { value: 'month', label: 'This month' },
  { value: 'last-month', label: 'Last month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'year', label: 'Year' }
];

/** Your own hours and what they are worth, by day or by client. */
export function PersonalHoursScreen() {
  const session = useSession();
  const [period, setPeriod] = useState<PersonalHoursPeriod>('current-pay');
  const [by, setBy] = useState<'day' | 'client'>('day');
  const hours = usePersonalHours(session.user.id, period);
  const rows = hours.data ?? [];

  const totals = rows.reduce((t, r) => ({ hours: t.hours + r.time, amount: t.amount + r.amount }), { hours: 0, amount: 0 });
  const groups = useMemo(() => {
    const m = new Map<string, { hours: number; amount: number }>();
    for (const r of rows) {
      const k = by === 'day' ? r.date : (r.clientName ?? 'Unknown');
      const g = m.get(k) ?? { hours: 0, amount: 0 };
      m.set(k, { hours: g.hours + r.time, amount: g.amount + r.amount });
    }
    const list = [...m.entries()];
    return by === 'day' ? list.sort(([a], [b]) => (a < b ? 1 : -1)) : list.sort((a, b) => b[1].hours - a[1].hours);
  }, [rows, by]);

  return (
    <Screen title="Personal hours" back="/more" backLabel="More">
      <ChipRow value={period} onChange={setPeriod} options={PERIODS} />
      <div className="grid grid-cols-2 gap-2">
        <Stat label="Hours" value={hoursShort(totals.hours)} />
        <Stat label="Value" value={wholeMoney(totals.amount)} />
      </div>
      <Segmented
        value={by}
        onChange={setBy}
        options={[
          { value: 'day', label: 'By day' },
          { value: 'client', label: 'By client' }
        ]}
      />
      {hours.isPending ? (
        <SkeletonList />
      ) : groups.length === 0 ? (
        <Empty icon={<Clock />} title="No hours in this period" />
      ) : (
        <Group>
          {groups.map(([k, g]) => (
            <Row
              key={k}
              title={by === 'day' ? formatDay(k) : k}
              subtitle={wholeMoney(g.amount)}
              trailing={<span className="tnum font-medium">{hoursShort(g.hours)} h</span>}
            />
          ))}
        </Group>
      )}
    </Screen>
  );
}
